import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { useWeightConversion } from '@rematter/modules/trade/hooks/useWeightConversion';
import { enumLoadWeightType } from '@rematter/modules/trade/types/load';

import { NetQuantityFieldCellProps } from '../types';

import { WeightFieldCell } from '~/shared/components/line-items';

export const NetQuantityFieldCell: React.FC<NetQuantityFieldCellProps> = ({
  lineItem,
  disabled = false,
  onUpdateLineItem,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
  errors,
}) => {
  const { convertToDisplayUnit, convertFromDisplayUnit, massUnitAbbreviation } =
    useWeightConversion({
      weightUnit,
      facilityDefaultUnit,
      pricingUnit: lineItem.pricingUnit as Unit,
    });

  const handleChange = React.useCallback(
    (value: number) => {
      const storedUnit =
        (lineItem.weights?.[enumLoadWeightType.Net]?.unit as Unit) ||
        enumUnit.Pounds;
      const convertedValue = convertFromDisplayUnit(value, storedUnit);

      onUpdateLineItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Net]: {
            value: convertedValue,
            unit: storedUnit,
            __typename: 'Weight' as const,
          },
        },
      });
    },
    [lineItem.id, lineItem.weights, convertFromDisplayUnit, onUpdateLineItem],
  );

  const isDisabled = React.useMemo(
    () => disabled || lineItem.type === 'Tagged',
    [disabled, lineItem.type],
  );

  return (
    <WeightFieldCell
      value={convertToDisplayUnit(lineItem.weights?.[enumLoadWeightType.Net])}
      onChange={handleChange}
      disabled={isDisabled}
      prefix="N"
      suffix={massUnitAbbreviation}
      errorMessage={errors?.weights?.[enumLoadWeightType.Net]}
      isInvalid={!!errors?.weights?.[enumLoadWeightType.Net]}
      className="@NetQuantityFieldCell"
    />
  );
};
